import { useEffect, useRef, useState } from "react";
import type { DeckSpec, SlideSpec } from "../lib/claude";
import { useSettings } from "../store/settings";
import { useRH } from "../store/rh";
import { PERSONAS } from "../data/org";
import { initials, monogramColor } from "../lib/monogram";
import { speak, type SpeakController, type TTSVoice, TTS_VOICES } from "../lib/tts";
import { generateNarrationAI } from "../lib/claude";
import { profileOf } from "../data/profiles";
import type { DeckImages } from "../lib/pptxBuilder";

export interface PresentationViewProps {
  deck: DeckSpec;
  images: DeckImages;
  /** narrações já geradas (se não, geradora interna gera) */
  narrations?: string[];
  presenterId: string;
  onClose: () => void;
  /** contexto opcional pra IA gerar narração mais alinhada */
  clientContext?: string;
}

export function PresentationView(props: PresentationViewProps) {
  const settings = useSettings();
  const employees = useRH(s => s.employees);
  const presenter = PERSONAS.find(p => p.id === props.presenterId) ?? PERSONAS[0];

  const [idx, setIdx] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [narrations, setNarrations] = useState<string[]>(props.narrations ?? props.deck.slides.map(() => ""));
  const [genBusy, setGenBusy] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [autoAdvance, setAutoAdvance] = useState(true);
  const [voice, setVoice] = useState<TTSVoice>(settings.openaiKey ? "onyx" : "browser-default");
  const [speed, setSpeed] = useState(1.0);
  const ctrlRef = useRef<SpeakController | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const slide = props.deck.slides[idx];
  const img = props.images[idx] ?? null;
  const narration = narrations[idx] ?? "";
  const total = props.deck.slides.length;

  // === navegação por teclado ===
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" || e.key === " ") { e.preventDefault(); next(); }
      else if (e.key === "ArrowLeft") { prev(); }
      else if (e.key === "Escape") { stop(); props.onClose(); }
      else if (e.key === "f" || e.key === "F") { toggleFullscreen(); }
      else if (e.key === "p" || e.key === "P") { play(); }
      else if (e.key === "s" || e.key === "S") { stop(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const next = () => { stop(); setIdx(i => Math.min(i + 1, total - 1)); };
  const prev = () => { stop(); setIdx(i => Math.max(i - 1, 0)); };

  const stop = () => {
    ctrlRef.current?.stop();
    ctrlRef.current = null;
    setPlaying(false);
  };

  const play = async () => {
    if (!narration.trim()) {
      alert("Sem narração pra este slide. Clique '🎤 gerar narrações' primeiro.");
      return;
    }
    stop();
    setPlaying(true);
    try {
      const ctrl = await speak({
        text: narration, voice, speed,
        apiKey: voice === "browser-default" ? undefined : settings.openaiKey
      });
      ctrlRef.current = ctrl;
      await ctrl.done;
      ctrlRef.current = null;
      setPlaying(false);
      if (autoAdvance && idx < total - 1) {
        setTimeout(() => setIdx(i => i + 1), 400);
      }
    } catch (e) {
      alert("Falha no TTS: " + (e as Error).message);
      setPlaying(false);
    }
  };

  // auto-play quando muda de slide e autoAdvance está ligado e estava tocando antes
  useEffect(() => {
    if (autoAdvance && narration && playing) {
      play();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx]);

  const generateNarrations = async () => {
    if (!settings.anthropicKey) { alert("Configure chave Anthropic em ⚙️."); return; }
    setGenBusy(true);
    try {
      const prof = profileOf(presenter.id, presenter.tier);
      const ns = await generateNarrationAI({
        apiKey: settings.anthropicKey, model: settings.model,
        deck: props.deck,
        presenterName: presenter.name,
        presenterVoice: prof.voice,
        clientContext: props.clientContext
      });
      setNarrations(ns);
    } catch (e) {
      alert("Falha: " + (e as Error).message);
    } finally { setGenBusy(false); }
  };

  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
      await containerRef.current?.requestFullscreen?.();
      setFullscreen(true);
    } else {
      await document.exitFullscreen?.();
      setFullscreen(false);
    }
  };

  useEffect(() => {
    const onFs = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  useEffect(() => () => stop(), []); // cleanup ao desmontar

  const primary = props.deck.brandPrimary ?? "#121214";
  const secondary = props.deck.brandSecondary ?? "#A8863C";
  const accent = props.deck.brandAccent ?? "#F2EEE6";

  const presenterAvatar = employees[presenter.id]?.avatarUrl;

  return (
    <div className="fixed inset-0 z-[60] bg-ink/95 flex items-center justify-center"
         onClick={props.onClose}>
      <div ref={containerRef}
           className="relative w-full h-full max-w-[1800px] max-h-[1000px] m-4 flex flex-col"
           style={{ background: accent }}
           onClick={(e) => e.stopPropagation()}>

        {/* HEADER */}
        <header className="px-5 py-3 flex items-center justify-between shrink-0 border-b-2 border-ink"
                style={{ background: primary, color: accent }}>
          <div className="flex items-center gap-3 min-w-0">
            <div className="font-mono text-[10px] opacity-70">APRES-LIVE</div>
            <div className="font-display font-black uppercase text-lg truncate">{props.deck.title}</div>
            <span className="tag bg-bone text-ink px-2">{idx + 1} / {total}</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={toggleFullscreen}
              className="btn bg-bone text-ink border-bone" title="F">
              {fullscreen ? "↙ janela" : "⛶ tela cheia"}
            </button>
            <button onClick={() => { stop(); props.onClose(); }}
              className="btn bg-red text-bone border-red">×</button>
          </div>
        </header>

        {/* SLIDE + NARRATOR */}
        <div className="flex-1 flex overflow-hidden">
          {/* slide grande */}
          <div className="flex-1 flex items-center justify-center p-6"
               style={{
                 background: `radial-gradient(circle at 30% 30%, ${primary}15 0%, ${accent} 60%)`
               }}>
            <SlideBig slide={slide} img={img} idx={idx}
                      primary={primary} secondary={secondary} accent={accent} />
          </div>

          {/* narrator panel */}
          <aside className="w-80 shrink-0 border-l-2 border-ink flex flex-col"
                 style={{ background: primary, color: accent }}>
            <div className="p-4 border-b border-bone/20">
              <div className="flex items-center gap-3 mb-2">
                <div className={`w-14 h-14 flex items-center justify-center font-display font-black text-xl overflow-hidden transition
                                ${playing ? "ring-4 ring-gold animate-pulse" : ""}`}
                     style={{ background: monogramColor(presenter.id) }}>
                  {presenterAvatar
                    ? <img src={presenterAvatar} alt="" className="w-full h-full object-cover" />
                    : initials(presenter.name)}
                </div>
                <div className="min-w-0">
                  <div className="font-mono text-[10px] opacity-60">APRESENTADOR</div>
                  <div className="font-display font-bold uppercase leading-tight">{presenter.name}</div>
                  {playing && <Waveform />}
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 text-[13px] leading-relaxed font-serif">
              {narration ? (
                <p className={playing ? "opacity-100" : "opacity-80"}>
                  "{narration}"
                </p>
              ) : (
                <div className="font-mono text-[11px] opacity-60 italic">
                  Sem narração ainda. Clique <span className="font-bold not-italic">🎤 gerar narrações</span> abaixo pra Claude escrever o roteiro falado de todos os slides.
                </div>
              )}
            </div>

            {/* controles */}
            <div className="border-t border-bone/20 p-3 space-y-2 shrink-0">
              {narrations.every(n => !n) && (
                <button onClick={generateNarrations} disabled={genBusy}
                  className="btn bg-gold text-ink border-gold w-full py-2 disabled:opacity-50">
                  {genBusy ? "🎤 escrevendo roteiro…" : "🎤 gerar narrações com IA"}
                </button>
              )}
              <div className="flex gap-1">
                {!playing ? (
                  <button onClick={play} disabled={!narration}
                    className="btn bg-bone text-ink border-bone flex-1 disabled:opacity-40">
                    ▶ ouvir slide
                  </button>
                ) : (
                  <button onClick={stop} className="btn bg-red text-bone border-red flex-1">
                    ⏸ parar
                  </button>
                )}
              </div>
              <details className="text-[11px]">
                <summary className="cursor-pointer opacity-70">⚙ voz / velocidade</summary>
                <div className="mt-2 space-y-1">
                  <select value={voice} onChange={(e) => setVoice(e.target.value as TTSVoice)}
                          className="w-full bg-bone text-ink px-1 py-0.5 font-mono text-[11px]">
                    {TTS_VOICES.map(v => (
                      <option key={v.id} value={v.id}
                              disabled={v.id !== "browser-default" && !settings.openaiKey}>
                        {v.label}
                      </option>
                    ))}
                  </select>
                  <div className="flex items-center gap-2">
                    <span className="opacity-60">vel</span>
                    <input type="range" min={0.7} max={1.5} step={0.05}
                           value={speed} onChange={(e) => setSpeed(Number(e.target.value))}
                           className="flex-1" />
                    <span className="font-mono w-8 text-right">{speed.toFixed(2)}x</span>
                  </div>
                  <label className="flex items-center gap-1">
                    <input type="checkbox" checked={autoAdvance}
                           onChange={(e) => setAutoAdvance(e.target.checked)} />
                    auto-avançar
                  </label>
                </div>
              </details>
              <div className="text-[10px] opacity-50 font-mono">
                ← → · espaço · F · P · S · ESC
              </div>
            </div>
          </aside>
        </div>

        {/* BOTTOM CONTROLS */}
        <footer className="px-5 py-3 flex items-center gap-2 shrink-0 border-t-2 border-ink"
                style={{ background: primary, color: accent }}>
          <button onClick={prev} disabled={idx === 0}
            className="btn bg-bone text-ink border-bone disabled:opacity-40">← anterior</button>

          {/* progress bar */}
          <div className="flex-1 flex gap-1 px-3">
            {props.deck.slides.map((_, i) => (
              <button key={i}
                onClick={() => { stop(); setIdx(i); }}
                className={`h-1.5 flex-1 transition rounded-full
                  ${i === idx ? "bg-bone" : i < idx ? "bg-bone/50" : "bg-bone/15"}`} />
            ))}
          </div>

          <button onClick={next} disabled={idx >= total - 1}
            className="btn bg-bone text-ink border-bone disabled:opacity-40">próximo →</button>
        </footer>
      </div>
    </div>
  );
}

/* ==================== SLIDE BIG ==================== */
function SlideBig({ slide, img, idx, primary, secondary, accent }:
  { slide: SlideSpec; img: string | null; idx: number;
    primary: string; secondary: string; accent: string }) {

  const layout = slide.layout ?? "bullets";

  // animation key forces remount on slide change
  const key = `slide-${idx}`;

  const wrapStyle = {
    width: "min(90%, 1400px)",
    aspectRatio: "16/9"
  } as const;

  if (layout === "cover") {
    return (
      <div key={key} className="relative overflow-hidden border-4 border-ink animate-fadeIn"
           style={{ ...wrapStyle, background: primary, color: accent }}>
        {img && (
          <>
            <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: primary, opacity: 0.45 }} />
          </>
        )}
        <div className="relative w-full h-full flex flex-col justify-end p-12">
          <div className="w-12 h-1.5 mb-6" style={{ background: secondary }} />
          <h1 className="font-display font-black uppercase leading-[0.85]"
              style={{ fontSize: "clamp(48px, 8vw, 120px)" }}>
            {slide.title}
          </h1>
          {slide.subtitle && (
            <p className="font-serif italic mt-4" style={{ fontSize: "clamp(20px, 2.5vw, 34px)" }}>
              {slide.subtitle}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (layout === "section") {
    return (
      <div key={key} className="relative overflow-hidden border-4 border-ink animate-fadeIn flex items-center justify-center"
           style={{ ...wrapStyle, background: primary, color: accent }}>
        <h1 className="font-display font-black uppercase text-center px-8"
            style={{ fontSize: "clamp(60px, 10vw, 160px)", lineHeight: 0.9 }}>
          {slide.title}
        </h1>
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 w-20 h-2"
             style={{ background: secondary }} />
      </div>
    );
  }

  if (layout === "metric") {
    return (
      <div key={key} className="relative overflow-hidden border-4 border-ink animate-fadeIn flex flex-col items-center justify-center"
           style={{ ...wrapStyle, background: accent, color: primary }}>
        <div className="font-mono text-xs uppercase opacity-60 mb-4">{slide.title}</div>
        <div className="font-display font-black leading-none"
             style={{ fontSize: "clamp(120px, 20vw, 280px)", color: secondary }}>
          {slide.metric?.value ?? "—"}
        </div>
        {slide.metric?.caption && (
          <div className="font-serif italic mt-6 max-w-[80%] text-center"
               style={{ fontSize: "clamp(18px, 2vw, 28px)" }}>
            {slide.metric.caption}
          </div>
        )}
      </div>
    );
  }

  if (layout === "quote") {
    return (
      <div key={key} className="relative overflow-hidden border-4 border-ink animate-fadeIn flex flex-col items-center justify-center px-16"
           style={{ ...wrapStyle, background: primary, color: accent }}>
        <div style={{ fontSize: "clamp(120px, 12vw, 200px)", color: secondary, lineHeight: 0.5 }} className="font-serif">"</div>
        <blockquote className="font-serif italic text-center max-w-4xl"
                    style={{ fontSize: "clamp(28px, 3.5vw, 52px)" }}>
          {slide.quote?.text}
        </blockquote>
        <div className="font-mono uppercase mt-8" style={{ color: secondary }}>
          — {slide.quote?.author}
        </div>
      </div>
    );
  }

  if (layout === "closing") {
    return (
      <div key={key} className="relative overflow-hidden border-4 border-ink animate-fadeIn p-12"
           style={{ ...wrapStyle, background: secondary, color: primary }}>
        <h1 className="font-display font-black uppercase mb-8"
            style={{ fontSize: "clamp(40px, 6vw, 80px)", lineHeight: 0.9 }}>
          {slide.title}
        </h1>
        {slide.bullets && (
          <ol className="space-y-3">
            {slide.bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-4">
                <span className="font-display font-black opacity-30 shrink-0"
                      style={{ fontSize: "clamp(24px, 3vw, 40px)" }}>
                  {String(i+1).padStart(2,"0")}
                </span>
                <span className="font-serif italic" style={{ fontSize: "clamp(18px, 2vw, 30px)" }}>{b}</span>
              </li>
            ))}
          </ol>
        )}
      </div>
    );
  }

  if ((layout === "image-left" || layout === "image-right") && img) {
    const imgLeft = layout === "image-left";
    return (
      <div key={key} className="relative overflow-hidden border-4 border-ink animate-fadeIn flex"
           style={{ ...wrapStyle, background: accent, color: primary }}>
        {imgLeft && <img src={img} alt="" className="w-1/2 h-full object-cover" />}
        <div className={`w-1/2 flex flex-col justify-center p-10 ${imgLeft ? "" : "order-first"}`}>
          <div className="w-10 h-1 mb-4" style={{ background: secondary }} />
          <h2 className="font-display font-black uppercase leading-tight"
              style={{ fontSize: "clamp(32px, 4vw, 56px)" }}>
            {slide.title}
          </h2>
          {slide.body && (
            <p className="font-serif italic mt-4" style={{ fontSize: "clamp(16px, 1.7vw, 22px)" }}>
              {slide.body}
            </p>
          )}
          {slide.bullets && (
            <ul className="mt-4 space-y-2">
              {slide.bullets.map((b, i) => (
                <li key={i} className="flex gap-2" style={{ fontSize: "clamp(15px, 1.5vw, 20px)" }}>
                  <span style={{ color: secondary }}>▪</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        {!imgLeft && <img src={img} alt="" className="w-1/2 h-full object-cover" />}
      </div>
    );
  }

  // bullets default
  return (
    <div key={key} className="relative overflow-hidden border-4 border-ink animate-fadeIn p-10"
         style={{ ...wrapStyle, background: accent, color: primary }}>
      <div className="w-12 h-1.5 mb-6" style={{ background: secondary }} />
      <h2 className="font-display font-black uppercase mb-6 leading-tight"
          style={{ fontSize: "clamp(40px, 5vw, 72px)" }}>
        {slide.title}
      </h2>
      {slide.body && (
        <p className="font-serif italic mb-6 max-w-4xl" style={{ fontSize: "clamp(18px, 2vw, 26px)" }}>
          {slide.body}
        </p>
      )}
      {slide.bullets && (
        <ul className="space-y-4 max-w-4xl">
          {slide.bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-4" style={{ fontSize: "clamp(20px, 2.2vw, 32px)" }}>
              <span className="font-display font-black opacity-30 shrink-0"
                    style={{ fontSize: "clamp(18px, 1.8vw, 26px)" }}>
                {String(i+1).padStart(2,"0")}
              </span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}
      {img && (
        <img src={img} alt=""
             className="absolute bottom-8 right-8 w-1/4 h-1/3 object-cover border-2 border-ink shadow-[6px_6px_0_0_currentColor]" />
      )}
    </div>
  );
}

function Waveform() {
  return (
    <div className="flex items-center gap-0.5 mt-1">
      {[...Array(12)].map((_, i) => (
        <span key={i}
              className="w-0.5 bg-bone animate-pulse"
              style={{
                height: `${4 + (i % 5) * 3}px`,
                animationDelay: `${i * 80}ms`,
                animationDuration: "600ms"
              }} />
      ))}
    </div>
  );
}
